/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import CanvasTextureDictionary from "App/utils/text/canvas_texture_dictionary";
import {vec3, vec4} from "gl-matrix";
import BoundingBox from "App/utils/bounding_box";
import IScene from "js_interface/src/scene_interface";

export default
class CanvasTextGenerator {

    private _sceneAPI: IScene;
    private _fontAndStyleMap: {} = {};
    private _texturesNeedUpdate: boolean = false;

    constructor(aSceneAPI: IScene) {
        this._sceneAPI = aSceneAPI;
    }

    public addCharToDictionary(aChar: string, aFontStyle: string, aFontSize: number, aFontName: string, aOutlineSize: number): any {
        let fontAndStyleUID = aFontName + aFontStyle;
        let fsMapItem = this._fontAndStyleMap[fontAndStyleUID];
        // get or create font adn style item from the map as we need separate
        if (fsMapItem === undefined) {
            fsMapItem = {
                dictionaries: []
            };
            this._fontAndStyleMap[fontAndStyleUID] = fsMapItem;
        }

        // look for char in the dictionary
        for (let i = 0; i < fsMapItem.dictionaries.length; i++) {
            let metric = fsMapItem.dictionaries[i].addChar(aChar, aFontStyle, aFontSize, aFontName, aOutlineSize);
            if (metric) {
                return metric;
            }
        }

        this._texturesNeedUpdate = true;

        // create new dictionary as there is no space in current dictionaries
        let dictionary = new CanvasTextureDictionary(this._sceneAPI);
        fsMapItem.dictionaries.push(dictionary);
        return dictionary.addChar(aChar, aFontStyle, aFontSize, aFontName, aOutlineSize);
    }

    public createTextGeometry(aString: string, aPosition: vec3, aFontStyle: string, aFontSize: number, aFontName: string, aColor?: vec4, aOutlineColor?: vec4, aIconUrl?: string, aIconSize?: number): any {
        let geometry: any = {
            vertexCount: 0,
            normalCount: 0,
            texcoordCount: 0,
            indexCount: 0,
            vertices: [],
            normals: [],
            texCoords: [],
            indices: [],
            canvasTextureUri: null,
            bbox: new BoundingBox(),
            canvasTextureWidth: 0,
            canvasTextureHeight: 0
        };

        let scale = 3;
        let indexCounter = 0;
        let positionOffset = [0, 0, 0];//[aPosition[0], aPosition[1], aPosition[2]];
        for (let i = 0; i < aString.length; i++) {
            let char = aString[i];

            let charMetric = this.addCharToDictionary(char, aFontStyle, aFontSize, aFontName, 0.5);

            geometry.canvasTextureUri = charMetric.canvasTextureUri;
            geometry.canvasTextureWidth = charMetric.canvasTextureWidth;
            geometry.canvasTextureHeight = charMetric.canvasTextureHeight;

            // we need 4 point for each glyph
            // left bottom point
            geometry.vertices.push(positionOffset[0]);
            geometry.vertices.push(positionOffset[1]);
            geometry.vertices.push(positionOffset[2]);
            geometry.bbox.extendByXYZ(geometry.vertices[geometry.vertices.length - 3], geometry.vertices[geometry.vertices.length - 2], geometry.vertices[geometry.vertices.length - 1]);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.texCoords.push(charMetric.normalizedPosition.left);
            geometry.texCoords.push(charMetric.normalizedPosition.top);

            // right bottom point
            geometry.vertices.push(positionOffset[0] + charMetric.normalizedDimension.width * scale);
            geometry.vertices.push(positionOffset[1]);
            geometry.vertices.push(positionOffset[2]);
            geometry.bbox.extendByXYZ(geometry.vertices[geometry.vertices.length - 3], geometry.vertices[geometry.vertices.length - 2], geometry.vertices[geometry.vertices.length - 1]);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.texCoords.push(charMetric.normalizedPosition.left + charMetric.normalizedDimension.width);
            geometry.texCoords.push(charMetric.normalizedPosition.top);

            // right top point
            geometry.vertices.push(positionOffset[0] + charMetric.normalizedDimension.width * scale);
            geometry.vertices.push(positionOffset[1] + charMetric.normalizedDimension.height * scale);
            geometry.vertices.push(positionOffset[2]);
            geometry.bbox.extendByXYZ(geometry.vertices[geometry.vertices.length - 3], geometry.vertices[geometry.vertices.length - 2], geometry.vertices[geometry.vertices.length - 1]);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.texCoords.push(charMetric.normalizedPosition.left + charMetric.normalizedDimension.width);
            geometry.texCoords.push(charMetric.normalizedPosition.top - charMetric.normalizedDimension.height);

            // left top point
            geometry.vertices.push(positionOffset[0]);
            geometry.vertices.push(positionOffset[1] + charMetric.normalizedDimension.height * scale);
            geometry.vertices.push(positionOffset[2]);
            geometry.bbox.extendByXYZ(geometry.vertices[geometry.vertices.length - 3], geometry.vertices[geometry.vertices.length - 2], geometry.vertices[geometry.vertices.length - 1]);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.normals.push(0);
            geometry.texCoords.push(charMetric.normalizedPosition.left);
            geometry.texCoords.push(charMetric.normalizedPosition.top - charMetric.normalizedDimension.height);

            // indices
            geometry.indices.push(indexCounter + 0);
            geometry.indices.push(indexCounter + 1);
            geometry.indices.push(indexCounter + 2);
            geometry.indices.push(indexCounter + 0);
            geometry.indices.push(indexCounter + 2);
            geometry.indices.push(indexCounter + 3);

            indexCounter += 4; // we have 4 new vertices
            positionOffset[0] += charMetric.normalizedDimension.width * scale;
        }

        geometry.vertexCount = geometry.vertices.length / 3;
        geometry.normalCount = geometry.normals.length / 3;
        geometry.texcoordCount = geometry.texCoords.length / 2;
        geometry.indexCount = geometry.indices.length;

        return geometry;
    }

    public tick(): void {
        if (!this._texturesNeedUpdate) {
            return;
        }

        let updateInProgress = false;
        for (let key in this._fontAndStyleMap) {
            let fsMapItem = this._fontAndStyleMap[key];

            for (var i = 0; i < fsMapItem.dictionaries.length; i++) {
                updateInProgress = updateInProgress || fsMapItem.dictionaries[i].updateTexture();
            }
        }

        this._texturesNeedUpdate = updateInProgress;
    }
}
